import type { CheckboxRootProps } from "@base-ui/react/checkbox";
import { Checkbox as BaseCheckbox } from "@base-ui/react/checkbox";
import clsx from "clsx";
import type React from "react";
import { forwardRef, useId } from "react";
import type { LABEL_POSITIONS } from "../../utils/consts";
import { IconCheckbox, IconCheckboxCheckedFilled } from "../icons";
import {
  checkboxIndicatorClass,
  checkboxLabelClass,
  checkboxLabelColumnClass,
  checkboxLabelColumnReverseClass,
  checkboxLabelRowClass,
  checkboxLabelRowReverseClass,
  checkboxRootClass,
  checkboxTextColumnClass,
  checkboxWrapperClass,
} from "./styles.css";

interface CheckboxProps {
  label?: React.ReactNode;
  labelPosition?: keyof typeof LABEL_POSITIONS;
  description?: React.ReactNode;
  containerStyles?: React.CSSProperties;
  labelStyles?: React.CSSProperties;
  className?: string;
  checked?: boolean;
  defaultChecked?: boolean;
  disabled?: boolean;
  indeterminate?: boolean;
  /** Called with the new boolean checked value. Prefer this over onChange. */
  onCheckedChange?: (checked: boolean) => void;
  /** Legacy onChange for backward compatibility — receives a synthetic event with target.checked */
  onChange?: React.ChangeEventHandler<HTMLInputElement>;
  id?: string;
  name?: string;
  value?: string;
  required?: boolean;
  readOnly?: boolean;
  /** Base UI render prop — renders the checkbox root as a custom element. */
  render?: CheckboxRootProps["render"];
}

export const Checkbox = forwardRef(function Checkbox(
  {
    label,
    labelPosition = "right",
    labelStyles,
    containerStyles,
    className,
    description,
    checked,
    defaultChecked,
    disabled,
    indeterminate,
    onCheckedChange,
    onChange,
    id,
    name,
    value,
    required,
    readOnly,
    render,
  }: CheckboxProps,
  ref: React.Ref<HTMLElement>,
) {
  const reactId = useId();
  const controlId = id ?? reactId;
  const labelId = `${controlId}-label`;

  const handleCheckedChange = (isChecked: boolean) => {
    if (onChange) {
      onChange({
        target: { checked: isChecked, name: name ?? "" },
      } as React.ChangeEvent<HTMLInputElement>);
    }
    onCheckedChange?.(isChecked);
  };

  // DOM order: checkbox first, then text. Flex direction on wrapper controls visual order.
  const flexDirectionClassMap: Record<keyof typeof LABEL_POSITIONS, string> = {
    left: checkboxLabelRowReverseClass, // row-reverse → text left, checkbox right
    right: checkboxLabelRowClass, // row → checkbox left, text right
    top: checkboxLabelColumnReverseClass, // column-reverse → text top, checkbox bottom
    bottom: checkboxLabelColumnClass, // column → checkbox top, text bottom
  };
  const flexDirectionClass = flexDirectionClassMap[labelPosition];

  return (
    <div
      className={clsx(checkboxWrapperClass, flexDirectionClass, className)}
      style={containerStyles}
      data-slot="checkbox-root"
    >
      <BaseCheckbox.Root
        ref={ref}
        id={controlId}
        name={name}
        checked={checked}
        defaultChecked={defaultChecked}
        disabled={disabled}
        indeterminate={indeterminate}
        required={required}
        readOnly={readOnly}
        value={value}
        onCheckedChange={handleCheckedChange}
        className={checkboxRootClass}
        data-slot="checkbox-control"
        aria-labelledby={label ? labelId : undefined}
        {...(render !== undefined && { render })}
      >
        <IconCheckbox className="icon-not-checked" data-slot="checkbox-icon" />
        <BaseCheckbox.Indicator
          keepMounted
          className={checkboxIndicatorClass}
          data-slot="checkbox-indicator"
        >
          <IconCheckboxCheckedFilled className="icon-checked" />
        </BaseCheckbox.Indicator>
      </BaseCheckbox.Root>
      {(label || description) && (
        <div className={checkboxTextColumnClass}>
          {label && (
            <label
              id={labelId}
              htmlFor={controlId}
              className={checkboxLabelClass}
              aria-disabled={disabled}
              style={labelStyles}
              data-slot="checkbox-label"
            >
              {label}
            </label>
          )}
          {description && (
            <p
              aria-disabled={disabled}
              className="checkbox-description"
              data-slot="checkbox-description"
            >
              {description}
            </p>
          )}
        </div>
      )}
    </div>
  );
});

Checkbox.displayName = "Checkbox";
